import { getToken, getRefreshToken, setTokens, clearTokens } from "./auth";
import { getClientHeaders } from "./client-info";
import { getConfig } from "./config";

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
  ) {
    super(detail);
  }
}

export class ConnectionError extends ApiError {
  constructor(
    detail = "Ошибка подключения",
    public retry?: () => void,
    public retryAfterMs?: number,
  ) {
    super(0, detail);
  }
}

export const CONNECTION_RETRY_EVENT = "meridianhub:connection-retry";
export const CONNECTION_RETRY_CLEAR_EVENT = "meridianhub:connection-retry-clear";

const REQUEST_TIMEOUT_MS = 10_000;
const CONNECTION_RETRY_MS = 5_000;

let refreshPromise: Promise<RefreshResult> | null = null;

enum RefreshResult {
  refreshed = "refreshed",
  invalidSession = "invalidSession",
  retryLater = "retryLater",
}

const TOKEN_REFRESH_SKEW_SECONDS = 60;

function redirectToLogin() {
  if (typeof window === "undefined") return;
  // Don't redirect on public pages (invite, auth callback)
  const path = window.location.pathname + window.location.hash;
  if (path.includes("/invite/") || path.includes("/auth/callback")) return;

  if ((window as any).electronAPI) {
    window.location.hash = "#/login";
  } else {
    window.location.href = "/login";
  }
}

async function tryRefresh(): Promise<RefreshResult> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return RefreshResult.invalidSession;

  try {
    const res = await fetch(`${getConfig().passportUrl}/v1/passport/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getClientHeaders() },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (res.status === 401 || res.status === 403) return RefreshResult.invalidSession;
    if (!res.ok) return RefreshResult.retryLater;
    const data = await res.json();
    setTokens(data.access_token, data.refresh_token);
    return RefreshResult.refreshed;
  } catch {
    return RefreshResult.retryLater;
  }
}

function decodeJwtExp(token: string | null): number | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

function isTokenExpiringSoon(token: string | null): boolean {
  const exp = decodeJwtExp(token);
  if (!exp) return false;
  const now = Math.floor(Date.now() / 1000);
  return exp <= now + TOKEN_REFRESH_SKEW_SECONDS;
}

export async function ensureFreshSession(): Promise<void> {
  const token = getToken();
  if (!token || !isTokenExpiringSoon(token)) return;

  if (!refreshPromise) {
    refreshPromise = tryRefresh().finally(() => { refreshPromise = null; });
  }
  const refreshResult = await refreshPromise;
  if (refreshResult === RefreshResult.refreshed) return;
  if (refreshResult === RefreshResult.invalidSession) {
    clearTokens();
    redirectToLogin();
    throw new ApiError(401, "Unauthorized");
  }
  throw new ConnectionError(
    "Ошибка подключения",
    () => { void ensureFreshSession(); },
  );
}

async function request<T>(
  baseUrl: string,
  path: string,
  options: RequestInit = {},
  isRetry = false,
  connectionRetryScheduled = false,
): Promise<T> {
  const token = getToken();
  const method = (options.method || "GET").toUpperCase();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetchWithTimeout(`${baseUrl}${path}`, { ...options, headers });
  } catch (error) {
    if (isConnectionIssue(error)) {
      if (method === "GET" && !connectionRetryScheduled) {
        return scheduleConnectionRetry(() =>
          request<T>(baseUrl, path, options, isRetry, true)
        );
      }
      throw new ConnectionError(
        "Ошибка подключения",
        () => { void request<T>(baseUrl, path, options); },
      );
    }
    throw error;
  }

  if (res.status === 401 && !isRetry) {
    if (!refreshPromise) {
      refreshPromise = tryRefresh().finally(() => { refreshPromise = null; });
    }
    const refreshResult = await refreshPromise;
    if (refreshResult === RefreshResult.refreshed) {
      return request<T>(baseUrl, path, options, true);
    }
    if (refreshResult === RefreshResult.invalidSession) {
      // Refresh explicitly rejected by server — session truly expired
      clearTokens();
      redirectToLogin();
    } else if (refreshResult === RefreshResult.retryLater) {
      throw new ConnectionError(
        "Ошибка подключения",
        () => { void request<T>(baseUrl, path, options); },
      );
    }
    throw new ApiError(401, "Unauthorized");
  }

  if (res.status === 401) {
    // Already retried — don't redirect, just throw
    throw new ApiError(401, "Unauthorized");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: "Unknown error" }));
    throw new ApiError(res.status, body.detail || "Unknown error");
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

async function requestText(
  baseUrl: string,
  path: string,
  options: RequestInit = {},
  isRetry = false,
  connectionRetryScheduled = false,
): Promise<string> {
  const token = getToken();
  const method = (options.method || "GET").toUpperCase();
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetchWithTimeout(`${baseUrl}${path}`, { ...options, headers });
  } catch (error) {
    if (isConnectionIssue(error)) {
      if (method === "GET" && !connectionRetryScheduled) {
        return scheduleConnectionRetry(() =>
          requestText(baseUrl, path, options, isRetry, true)
        );
      }
      throw new ConnectionError(
        "Ошибка подключения",
        () => { void requestText(baseUrl, path, options); },
      );
    }
    throw error;
  }

  if (res.status === 401 && !isRetry) {
    if (!refreshPromise) {
      refreshPromise = tryRefresh().finally(() => { refreshPromise = null; });
    }
    const refreshResult = await refreshPromise;
    if (refreshResult === RefreshResult.refreshed) {
      return requestText(baseUrl, path, options, true);
    }
    if (refreshResult === RefreshResult.invalidSession) {
      clearTokens();
      redirectToLogin();
    } else if (refreshResult === RefreshResult.retryLater) {
      throw new ConnectionError(
        "Ошибка подключения",
        () => { void requestText(baseUrl, path, options); },
      );
    }
    throw new ApiError(401, "Unauthorized");
  }

  if (res.status === 401) {
    throw new ApiError(401, "Unauthorized");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: "Unknown error" }));
    throw new ApiError(res.status, body.detail || "Unknown error");
  }

  return res.text();
}

export function passport<T>(path: string, options: RequestInit = {}) {
  return request<T>(getConfig().passportUrl, path, options);
}

export function chat<T>(path: string, options: RequestInit = {}) {
  return request<T>(getConfig().chatUrl, path, options);
}

export function chatText(path: string, options: RequestInit = {}) {
  return requestText(getConfig().chatUrl, path, options);
}

async function fetchWithTimeout(url: string, options: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
}

function isConnectionIssue(error: unknown): boolean {
  return error instanceof TypeError ||
    (error instanceof DOMException && error.name === "AbortError");
}

function scheduleConnectionRetry<T>(retryRequest: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    let settled = false;

    const clearNotice = () => {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(CONNECTION_RETRY_CLEAR_EVENT, {
          detail: { id },
        }));
      }
    };

    const retry = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      clearNotice();
      retryRequest().then(resolve).catch(reject);
    };

    const timer = setTimeout(retry, CONNECTION_RETRY_MS);

    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(CONNECTION_RETRY_EVENT, {
        detail: { id, retryAfterMs: CONNECTION_RETRY_MS, retry },
      }));
    }
  });
}
